import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    AreaDto,
    BlockDtoBlockLayoutType,
    BlockDto,
    BlockDtoSeatCountDirection,
    BlockDtoTableShape,
    PlanDto,
    RowRestrictionDto,
    RowSettingsDto,
    RowSettingsDtoSeatCountDirection,
} from '../../../../clients/services';
import { PlanAreaSelection } from '../../../Tickets/plan/Plan';

type Props = {
    selectedBlock: BlockDto | null | undefined;
    selectedRowNr: number | null;
    setSelectedRowNr: (rowNr: number | null) => void;
    selectedArea: PlanAreaSelection | null;
    selectedAreaDto: AreaDto | null | undefined;
    selectedRowSettings: RowSettingsDto | undefined;
    selectedRowRestriction: RowRestrictionDto | undefined;
    selectableRows: number[];
    updateSelectedBlock: (updater: (block: BlockDto, plan: PlanDto) => void, summary: string) => void;
    updateSelectedArea: (updater: (area: AreaDto) => AreaDto, summary: string) => void;
    updateSelectedRowSettings: (updater: (rowSettings: RowSettingsDto) => void, summary: string) => void;
    updateSelectedRowRestriction: (updater: (restriction: RowRestrictionDto) => void, summary: string) => void;
};

export const PlanSelectionInspector: React.FC<Props> = ({
    selectedBlock,
    selectedRowNr,
    setSelectedRowNr,
    selectedArea,
    selectedAreaDto,
    selectedRowSettings,
    selectedRowRestriction,
    selectableRows,
    updateSelectedBlock,
    updateSelectedArea,
    updateSelectedRowSettings,
    updateSelectedRowRestriction,
}) => {
    const [rowSeatCountsInput, setRowSeatCountsInput] = useState('');
    const [rowSeatCountsError, setRowSeatCountsError] = useState<string | null>(null);

    const compactCheckboxSx = {
        m: 0,
        '& .MuiFormControlLabel-label': {
            fontSize: '0.78rem',
            lineHeight: 1.2,
        },
    };

    const getAreaDisplayTitle = (areaSelection: PlanAreaSelection, area: AreaDto) => {
        const fallbackId = area.id !== undefined && area.id !== null ? String(area.id) : '-';
        const nameOrId = area.comment && area.comment.trim().length > 0 ? area.comment.trim() : fallbackId;

        if (areaSelection.type === 'stage') {
            return `Bühne`;
        }
        if (areaSelection.type === 'corridor') {
            return `Gang`;
        }
        if (areaSelection.type === 'mixer') {
            return `Mischpult`;
        }
        return `Eingang ${nameOrId}`;
    };

    const parseRotationValue = (raw: string): number | undefined | null => {
        if (raw === '') {
            return undefined;
        }
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) {
            return null;
        }
        return parsed;
    };

    const getSeatCountForRow = (block: BlockDto, row: number): number => {
        const rowSetting = block.rowSettings?.find((candidate) => candidate.row === row);
        const override = rowSetting ? (rowSetting as any).seatsInRow : undefined;
        if (Number.isInteger(override) && override > 0) {
            return override;
        }
        return block.seatsInRow ?? 1;
    };

    const parseRowSeatCounts = (raw: string): number[] | null => {
        const parts = raw
            .split(/[;,\s]+/)
            .map((part) => part.trim())
            .filter((part) => part.length > 0);
        if (!parts.length) {
            return null;
        }

        const values: number[] = [];
        for (const part of parts) {
            const parsed = Number(part);
            if (!Number.isInteger(parsed) || parsed <= 0) {
                return null;
            }
            values.push(parsed);
        }
        return values;
    };

    const isTableLayout = selectedBlock?.blockLayoutType === BlockDtoBlockLayoutType.Table;
    const isRadialLayout = selectedBlock?.blockLayoutType === BlockDtoBlockLayoutType.Radial;

    useEffect(() => {
        if (!selectedBlock || !isRadialLayout) {
            setRowSeatCountsInput('');
            setRowSeatCountsError(null);
            return;
        }

        const startRow = selectedBlock.startRowsAt ?? 1;
        const numberOfRows = selectedBlock.numberOfRows ?? 0;
        const counts =
            numberOfRows > 0
                ? Array.from({ length: numberOfRows }, (_, index) => getSeatCountForRow(selectedBlock, startRow + index))
                : [];

        setRowSeatCountsInput(counts.join(', '));
        setRowSeatCountsError(null);
    }, [selectedBlock, isRadialLayout]);

    const applyRowSeatCounts = () => {
        if (!selectedBlock) {
            return;
        }

        const counts = parseRowSeatCounts(rowSeatCountsInput);
        if (!counts) {
            setRowSeatCountsError('Bitte positive Ganzzahlen mit Komma, Leerzeichen oder Semikolon eingeben.');
            return;
        }

        updateSelectedBlock((block) => {
            const startRow = block.startRowsAt ?? 1;
            const endRow = startRow + counts.length - 1;

            block.numberOfRows = counts.length;
            block.seatsInRow = Math.max(...counts);

            if (!block.rowSettings) {
                block.rowSettings = [];
            }

            const rowSettingsList = block.rowSettings.filter(
                (rowSetting) => rowSetting.row >= startRow && rowSetting.row <= endRow,
            );
            block.rowSettings = rowSettingsList;

            counts.forEach((seatCount, index) => {
                const row = startRow + index;
                let rowSetting = rowSettingsList.find((candidate) => candidate.row === row) as
                    | RowSettingsDto
                    | undefined;

                if (!rowSetting) {
                    rowSetting = new RowSettingsDto({
                        row,
                        removeSeats: [],
                        gaps: [],
                        skipRow: false,
                        addCorridorAfter: false,
                        blockRow: false,
                    });
                    rowSettingsList.push(rowSetting);
                }

                (rowSetting as any).seatsInRow = seatCount;
            });
        }, 'Updated radial row seat counts');

        setRowSeatCountsError(null);
    };

    return (
        <>
            {selectedBlock && selectedRowNr === null && (
                <Stack spacing={1.25}>
                    <Typography variant="subtitle1">Block {selectedBlock.name ?? selectedBlock.id}</Typography>

                    <TextField
                        size="small"
                        label="Blockbezeichnung"
                        value={selectedBlock.name ?? ''}
                        fullWidth
                        onChange={(event) => {
                            const value = event.target.value;
                            updateSelectedBlock((block) => {
                                block.name = value;
                            }, 'Updated block name');
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={Boolean(selectedBlock.hideName)}
                                onChange={(event) => {
                                    updateSelectedBlock((block) => {
                                        block.hideName = event.target.checked;
                                    }, 'Updated hide block name');
                                }}
                            />
                        }
                        label="Blockbezeichnung ausblenden"
                        sx={compactCheckboxSx}
                    />

                    <FormControl size="small" fullWidth>
                        <InputLabel id="block-layout-type-label">Blocktyp</InputLabel>
                        <Select
                            labelId="block-layout-type-label"
                            label="Blocktyp"
                            value={selectedBlock.blockLayoutType ?? BlockDtoBlockLayoutType.Rows}
                            onChange={(event) => {
                                const nextType = event.target.value as BlockDtoBlockLayoutType;
                                updateSelectedBlock((block) => {
                                    block.blockLayoutType = nextType;
                                    if (nextType === BlockDtoBlockLayoutType.Table) {
                                        block.numberOfRows = 1;
                                        block.startRowsAt = 1;
                                        block.tableShape = block.tableShape ?? BlockDtoTableShape.Round;
                                        block.tableSeatCount = Math.max(
                                            1,
                                            block.tableSeatCount ?? block.seatsInRow ?? 8,
                                        );
                                        block.hasCenterTable = true;
                                        block.centerTableLabel = block.centerTableLabel ?? 'Tisch';
                                    } else if (nextType === BlockDtoBlockLayoutType.Radial) {
                                        block.numberOfRows = Math.max(2, block.numberOfRows ?? 5);
                                        block.startRowsAt = block.startRowsAt ?? 1;
                                        block.seatsInRow = Math.max(4, block.seatsInRow ?? block.tableSeatCount ?? 10);
                                        block.hasCenterTable = false;
                                        (block as any).radialOpenTop = (block as any).radialOpenTop ?? true;
                                        (block as any).radialRowSpacing =
                                            (block as any).radialRowSpacing ?? 30;
                                        (block as any).radialArcDegrees =
                                            (block as any).radialArcDegrees ?? 220;
                                    } else {
                                        block.seatsInRow = Math.max(1, block.seatsInRow ?? block.tableSeatCount ?? 8);
                                        block.hasCenterTable = false;
                                    }
                                }, 'Updated block layout type');
                            }}
                        >
                            <MenuItem value={BlockDtoBlockLayoutType.Rows}>Reihenblock</MenuItem>
                            <MenuItem value={BlockDtoBlockLayoutType.Table}>Tischblock</MenuItem>
                            <MenuItem value={BlockDtoBlockLayoutType.Radial}>Radialblock</MenuItem>
                        </Select>
                    </FormControl>

                    {!isTableLayout && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                                size="small"
                                type="number"
                                label="Anzahl Reihen"
                                value={selectedBlock.numberOfRows ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const parsed = Number(event.target.value);
                                    if (!Number.isInteger(parsed) || parsed <= 0) {
                                        return;
                                    }
                                    updateSelectedBlock((block) => {
                                        block.numberOfRows = parsed;
                                    }, 'Updated rows');
                                }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Sitze pro Reihe"
                                value={selectedBlock.seatsInRow ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const parsed = Number(event.target.value);
                                    if (!Number.isInteger(parsed) || parsed <= 0) {
                                        return;
                                    }
                                    updateSelectedBlock((block) => {
                                        block.seatsInRow = parsed;
                                    }, 'Updated seats per row');
                                }}
                            />
                        </Stack>
                    )}

                    {isRadialLayout && (
                        <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Innenkreis-Radius (px)"
                                    value={(selectedBlock as any).radialInnerRadius ?? ''}
                                    fullWidth
                                    helperText="Leer lassen = automatisch aus Sitzanzahl der vordersten Reihe"
                                    onChange={(event) => {
                                        const raw = event.target.value;
                                        if (raw === '') {
                                            updateSelectedBlock((block) => {
                                                (block as any).radialInnerRadius = undefined;
                                            }, 'Reset radial inner radius to automatic');
                                            return;
                                        }

                                        const parsed = Number(raw);
                                        if (!Number.isFinite(parsed) || parsed < 10) {
                                            return;
                                        }
                                        updateSelectedBlock((block) => {
                                            (block as any).radialInnerRadius = Math.round(parsed);
                                        }, 'Updated radial inner radius');
                                    }}
                                />
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Reihenabstand (px)"
                                    value={(selectedBlock as any).radialRowSpacing ?? 30}
                                    fullWidth
                                    onChange={(event) => {
                                        const parsed = Number(event.target.value);
                                        if (!Number.isFinite(parsed) || parsed < 8) {
                                            return;
                                        }
                                        updateSelectedBlock((block) => {
                                            (block as any).radialRowSpacing = Math.round(parsed);
                                        }, 'Updated radial row spacing');
                                    }}
                                />
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Abdeckwinkel (°)"
                                    value={(selectedBlock as any).radialArcDegrees ?? 220}
                                    fullWidth
                                    helperText="z.B. 45 für einen engen Fächer"
                                    onChange={(event) => {
                                        const parsed = Number(event.target.value);
                                        if (!Number.isFinite(parsed) || parsed <= 0 || parsed >= 360) {
                                            return;
                                        }
                                        updateSelectedBlock((block) => {
                                            (block as any).radialArcDegrees = Math.round(parsed);
                                        }, 'Updated radial arc degrees');
                                    }}
                                />
                            </Stack>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={(selectedBlock as any).radialOpenTop ?? true}
                                        onChange={(event) => {
                                            updateSelectedBlock((block) => {
                                                (block as any).radialOpenTop = event.target.checked;
                                            }, 'Updated radial opening direction');
                                        }}
                                    />
                                }
                                label="Öffnung nach oben (Bühnenseite)"
                                sx={compactCheckboxSx}
                            />

                            <TextField
                                size="small"
                                label="Sitze pro Reihe (vorne nach hinten)"
                                helperText={
                                    rowSeatCountsError ??
                                    'Zahlen mit Komma, Leerzeichen oder Semikolon trennen, z.B. 8,10,12,14'
                                }
                                error={Boolean(rowSeatCountsError)}
                                value={rowSeatCountsInput}
                                fullWidth
                                onChange={(event) => {
                                    setRowSeatCountsInput(event.target.value);
                                    if (rowSeatCountsError) {
                                        setRowSeatCountsError(null);
                                    }
                                }}
                                onBlur={applyRowSeatCounts}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        applyRowSeatCounts();
                                    }
                                }}
                            />
                        </Stack>
                    )}

                    {isTableLayout && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <FormControl size="small" fullWidth>
                                <InputLabel id="block-table-shape-label">Tischform</InputLabel>
                                <Select
                                    labelId="block-table-shape-label"
                                    label="Tischform"
                                    value={selectedBlock.tableShape ?? BlockDtoTableShape.Round}
                                    onChange={(event) => {
                                        const nextShape = event.target.value as BlockDtoTableShape;
                                        updateSelectedBlock((block) => {
                                            block.tableShape = nextShape;
                                        }, 'Updated table shape');
                                    }}
                                >
                                    <MenuItem value={BlockDtoTableShape.Round}>Rund</MenuItem>
                                    <MenuItem value={BlockDtoTableShape.Rect}>Rechteckig</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                type="number"
                                label="Sitzanzahl Tisch"
                                value={selectedBlock.tableSeatCount ?? 8}
                                fullWidth
                                onChange={(event) => {
                                    const parsed = Number(event.target.value);
                                    if (!Number.isInteger(parsed) || parsed <= 0) {
                                        return;
                                    }
                                    updateSelectedBlock((block) => {
                                        block.tableSeatCount = parsed;
                                    }, 'Updated table seat count');
                                }}
                            />
                        </Stack>
                    )}

                    {!isTableLayout && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                                size="small"
                                type="number"
                                label="Startreihe"
                                value={selectedBlock.startRowsAt ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const raw = event.target.value;
                                    const parsed = Number(raw);
                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                        return;
                                    }
                                    updateSelectedBlock((block) => {
                                        block.startRowsAt = raw === '' ? undefined : parsed;
                                    }, 'Updated start row');
                                }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Startplatznummer"
                                value={selectedBlock.startSeatNumbersAt ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const raw = event.target.value;
                                    const parsed = Number(raw);
                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                        return;
                                    }
                                    updateSelectedBlock((block) => {
                                        block.startSeatNumbersAt = raw === '' ? undefined : parsed;
                                    }, 'Startplatznummer angepasst');
                                }}
                            />
                        </Stack>
                    )}

                    {!isTableLayout && (
                        <FormControl size="small" fullWidth>
                            <InputLabel id="block-seat-direction-label">Zählrichtung der Sitzplätze</InputLabel>
                            <Select
                                labelId="block-seat-direction-label"
                                label="Zählrichtung der Sitzplätze"
                                value={selectedBlock.seatCountDirection ?? BlockDtoSeatCountDirection.LeftToRight}
                                onChange={(event) => {
                                    const direction = event.target.value as BlockDtoSeatCountDirection;
                                    updateSelectedBlock((block) => {
                                        block.seatCountDirection = direction;
                                    }, 'Zählrichtung der Sitzplätze angepasst');
                                }}
                            >
                                <MenuItem value={BlockDtoSeatCountDirection.LeftToRight}>links nach rechts</MenuItem>
                                <MenuItem value={BlockDtoSeatCountDirection.RightToLeft}>rechts nach links</MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    {isTableLayout && (
                        <TextField
                            size="small"
                            label="Tischbeschriftung"
                            value={selectedBlock.centerTableLabel ?? ''}
                            fullWidth
                            onChange={(event) => {
                                const value = event.target.value;
                                updateSelectedBlock((block) => {
                                    block.centerTableLabel = value;
                                }, 'Updated center table label');
                            }}
                        />
                    )}

                    {!isTableLayout && (
                        <Stack spacing={0}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(selectedBlock.blockWideSeatCount)}
                                        onChange={(event) => {
                                            updateSelectedBlock((block) => {
                                                block.blockWideSeatCount = event.target.checked;
                                                if (event.target.checked) {
                                                    block.hideSeatNr = true;
                                                }
                                            }, 'Updated block-wide seat count');
                                        }}
                                    />
                                }
                                label="Durch ganzen Block durchnumerieren"
                                sx={compactCheckboxSx}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(selectedBlock.blockAllSeats)}
                                        onChange={(event) => {
                                            updateSelectedBlock((block) => {
                                                block.blockAllSeats = event.target.checked;
                                            }, 'Updated block all seats');
                                        }}
                                    />
                                }
                                label="Alle Sitzplätze blockieren"
                                sx={compactCheckboxSx}
                            />

                            {!selectedBlock.blockWideSeatCount && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(selectedBlock.hideSeatNr)}
                                            onChange={(event) => {
                                                updateSelectedBlock((block) => {
                                                    block.hideSeatNr = event.target.checked;
                                                }, 'Sitznummern ausblenden angepasst');
                                            }}
                                        />
                                    }
                                    label="Sitznummern ausblenden"
                                    sx={compactCheckboxSx}
                                />
                            )}
                            {!selectedBlock.hideSeatNr && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(selectedBlock.showSeatNrAtTheEnd)}
                                            onChange={(event) => {
                                                updateSelectedBlock((block) => {
                                                    block.showSeatNrAtTheEnd = event.target.checked;
                                                }, 'Updated seat numbers at end');
                                            }}
                                        />
                                    }
                                    label="Sitznummern am Ende anzeigen"
                                    sx={compactCheckboxSx}
                                />
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(selectedBlock.hideRowNr)}
                                        onChange={(event) => {
                                            updateSelectedBlock((block) => {
                                                block.hideRowNr = event.target.checked;
                                            }, 'Updated hide row numbers');
                                        }}
                                    />
                                }
                                label="Reihennummern ausblenden"
                                sx={compactCheckboxSx}
                            />
                            {!selectedBlock.hideRowNr && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(selectedBlock.showRowNrRight)}
                                            onChange={(event) => {
                                                updateSelectedBlock((block) => {
                                                    block.showRowNrRight = event.target.checked;
                                                }, 'Updated row labels right');
                                            }}
                                        />
                                    }
                                    label="Reihennummer rechts anzeigen"
                                    sx={compactCheckboxSx}
                                />
                            )}
                        </Stack>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                            size="small"
                            type="number"
                            label="Blockausrichtung (°)"
                            value={selectedBlock.area?.rotation ?? 0}
                            fullWidth
                            inputProps={{ step: 1 }}
                            onChange={(event) => {
                                const parsed = parseRotationValue(event.target.value);
                                if (parsed === null) {
                                    return;
                                }
                                updateSelectedBlock((block) => {
                                    if (!block.area) {
                                        block.area = new AreaDto();
                                    }
                                    block.area.rotation = parsed;
                                }, 'Updated block area rotation');
                            }}
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Sitzausrichtung (°)"
                            value={selectedBlock.seatRotation ?? 0}
                            fullWidth
                            inputProps={{ step: 1 }}
                            onChange={(event) => {
                                const parsed = parseRotationValue(event.target.value);
                                if (parsed === null) {
                                    return;
                                }
                                updateSelectedBlock((block) => {
                                    block.seatRotation = parsed;
                                }, 'Updated seat rotation');
                            }}
                        />
                    </Stack>

                    <Divider />
                </Stack>
            )}

            {selectedBlock &&
                selectedRowNr !== null &&
                selectedBlock.blockLayoutType !== BlockDtoBlockLayoutType.Table && (
                    <Stack spacing={1.25}>
                        <Typography variant="subtitle1">
                            Row {selectedRowNr} in {selectedBlock.name ?? `Block ${selectedBlock.id}`}
                        </Typography>

                        <FormControl size="small" fullWidth>
                            <InputLabel id="selected-row-label">Selected Row</InputLabel>
                            <Select
                                labelId="selected-row-label"
                                label="Selected Row"
                                value={selectedRowNr}
                                onChange={(event) => {
                                    const value = Number(event.target.value);
                                    setSelectedRowNr(Number.isInteger(value) ? value : null);
                                }}
                            >
                                {selectableRows.map((rowNr) => (
                                    <MenuItem key={rowNr} value={rowNr}>
                                        Row {rowNr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <TextField
                                size="small"
                                type="number"
                                label="Sitze in dieser Reihe"
                                value={selectedRowSettings?.seatsInRow ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const raw = event.target.value;
                                    const parsed = Number(raw);
                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                        return;
                                    }
                                    updateSelectedRowSettings((rowSettings) => {
                                        rowSettings.seatsInRow = raw === '' ? undefined : parsed;
                                    }, 'Updated row seats in row');
                                }}
                            />

                            <TextField
                                size="small"
                                type="number"
                                label="Start Seat"
                                value={selectedRowSettings?.startSeatNumbersAt ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const raw = event.target.value;
                                    const parsed = Number(raw);
                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                        return;
                                    }
                                    updateSelectedRowSettings((rowSettings) => {
                                        rowSettings.startSeatNumbersAt = raw === '' ? undefined : parsed;
                                    }, 'Updated row start seat');
                                }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="End Seat"
                                value={selectedRowSettings?.endSeatNumberAt ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const raw = event.target.value;
                                    const parsed = Number(raw);
                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                        return;
                                    }
                                    updateSelectedRowSettings((rowSettings) => {
                                        rowSettings.endSeatNumberAt = raw === '' ? undefined : parsed;
                                    }, 'Updated row end seat');
                                }}
                            />
                        </Stack>

                        <FormControl size="small" fullWidth>
                            <InputLabel id="row-seat-direction-label">Seat Direction</InputLabel>
                            <Select
                                labelId="row-seat-direction-label"
                                label="Seat Direction"
                                value={
                                    selectedRowSettings?.seatCountDirection ??
                                    RowSettingsDtoSeatCountDirection.LikeBlockDefinition
                                }
                                onChange={(event) => {
                                    const direction = event.target.value as RowSettingsDtoSeatCountDirection;
                                    updateSelectedRowSettings((rowSettings) => {
                                        rowSettings.seatCountDirection = direction;
                                    }, 'Updated row seat direction');
                                }}
                            >
                                <MenuItem value={RowSettingsDtoSeatCountDirection.LikeBlockDefinition}>
                                    Like Block
                                </MenuItem>
                                <MenuItem value={RowSettingsDtoSeatCountDirection.LeftToRight}>Left To Right</MenuItem>
                                <MenuItem value={RowSettingsDtoSeatCountDirection.RightToLeft}>Right To Left</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(selectedRowSettings?.skipRow)}
                                    onChange={(event) => {
                                        updateSelectedRowSettings((rowSettings) => {
                                            rowSettings.skipRow = event.target.checked;
                                        }, 'Updated skip row');
                                    }}
                                />
                            }
                            label="Skip Row"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(selectedRowSettings?.addCorridorAfter)}
                                    onChange={(event) => {
                                        updateSelectedRowSettings((rowSettings) => {
                                            rowSettings.addCorridorAfter = event.target.checked;
                                        }, 'Updated add corridor after row');
                                    }}
                                />
                            }
                            label="Add Corridor After"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(selectedRowSettings?.blockRow)}
                                    onChange={(event) => {
                                        updateSelectedRowSettings((rowSettings) => {
                                            rowSettings.blockRow = event.target.checked;
                                        }, 'Updated block row');
                                    }}
                                />
                            }
                            label="Block Entire Row"
                        />

                        <Accordion
                            elevation={0}
                            disableGutters
                            sx={{
                                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                '&:before': {
                                    display: 'none',
                                },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle2">Row Restrictions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={1.25}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(selectedRowRestriction?.noRestriction)}
                                                onChange={(event) => {
                                                    updateSelectedRowRestriction((restriction) => {
                                                        restriction.noRestriction = event.target.checked;
                                                    }, 'Updated row restriction no restriction');
                                                }}
                                            />
                                        }
                                        label="No Restriction"
                                    />

                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Empty Seats In Between Override"
                                        value={selectedRowRestriction?.emptySeatsInBetween ?? ''}
                                        fullWidth
                                        onChange={(event) => {
                                            const raw = event.target.value;
                                            const parsed = Number(raw);
                                            if (raw !== '' && (!Number.isInteger(parsed) || parsed < 0)) {
                                                return;
                                            }
                                            updateSelectedRowRestriction((restriction) => {
                                                restriction.emptySeatsInBetween = raw === '' ? undefined : parsed;
                                            }, 'Updated row restriction empty seats in between');
                                        }}
                                    />
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        <Button variant="outlined" onClick={() => setSelectedRowNr(null)}>
                            Back To Block
                        </Button>
                    </Stack>
                )}

            {selectedAreaDto && selectedArea && (
                <Stack spacing={1.25}>
                    <Typography variant="subtitle1">{getAreaDisplayTitle(selectedArea, selectedAreaDto)}</Typography>

                    <TextField
                        size="small"
                        label="Name"
                        value={selectedAreaDto.comment ?? ''}
                        fullWidth
                        onChange={(event) => {
                            const value = event.target.value;
                            updateSelectedArea((area) => {
                                const nextArea = AreaDto.fromJS(area.toJSON());
                                nextArea.comment = value;
                                return nextArea;
                            }, 'Updated area comment');
                        }}
                    />

                    <TextField
                        size="small"
                        label="Breite (in px oder %)"
                        value={selectedAreaDto.contentWidth ?? ''}
                        fullWidth
                        onChange={(event) => {
                            const value = event.target.value;
                            updateSelectedArea((area) => {
                                const nextArea = AreaDto.fromJS(area.toJSON());
                                nextArea.contentWidth = value;
                                return nextArea;
                            }, 'Updated area content width');
                        }}
                    />

                    <TextField
                        size="small"
                        type="number"
                        label="Blockausrichtung (°)"
                        value={selectedAreaDto.rotation ?? 0}
                        fullWidth
                        inputProps={{ step: 1 }}
                        onChange={(event) => {
                            const parsed = parseRotationValue(event.target.value);
                            if (parsed === null) {
                                return;
                            }
                            updateSelectedArea((area) => {
                                const nextArea = AreaDto.fromJS(area.toJSON());
                                nextArea.rotation = parsed;
                                return nextArea;
                            }, 'Updated area rotation');
                        }}
                    />
                </Stack>
            )}
        </>
    );
};
